// HR Center announcements (Nov 3, 2026).
//   • AnnouncementsTab — the HR Center tab: write one, pick who gets it (the
//     HR Policies picker), and see how many have clicked OK.
//   • AnnouncementSplash — mounted in Layout: shows each unacknowledged
//     announcement until the user clicks OK, and reopens one when its (already
//     read) bell notification is clicked (OPEN_ANNOUNCEMENT_EVENT).
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Megaphone, Plus } from "lucide-react";
import {
  fetchAnnouncements, createAnnouncement, fetchMyPendingAnnouncements,
  fetchMyAnnouncement, acknowledgeAnnouncement,
} from "../api/announcements";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "./DataTable";
import { EmployeeChecklist } from "./HrPolicies";
import Modal from "./Modal";
import { toast, apiErrorMessage } from "../utils/toast";
import { confirmDialog } from "../utils/confirm";
import {
  cardCls, inputCls, textareaCls, labelCls, buttonPrimaryCls, buttonSecondaryCls, buttonPrimarySmCls,
} from "../styles/ui";

export const OPEN_ANNOUNCEMENT_EVENT = "stinte:open-announcement";

const fmt = (iso, f = "MMM d, yyyy") => {
  const d = iso ? DateTime.fromISO(iso) : null;
  return d?.isValid ? d.toFormat(f) : "—";
};

function NewAnnouncementModal({ users, onClose }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return toast.error("Enter a title.");
    if (!body.trim()) return toast.error("Enter the announcement.");
    if (!selected.size) return toast.error("Pick who to send it to.");
    const ok = await confirmDialog({
      title: "Send this announcement?",
      message: `${selected.size} ${selected.size === 1 ? "person" : "people"} will see it the next time they open the portal, and must click OK to continue.`,
      confirmLabel: "Send",
    });
    if (!ok) return;
    setSaving(true);
    try {
      const a = await createAnnouncement(title.trim(), body.trim(), [...selected]);
      toast.success(`Sent to ${a.recipient_count} people.`);
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not send the announcement"));
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title="New announcement" closeOnEscape={false} closeOnBackdrop={false}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Title</label>
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus />
        </div>
        <div>
          <label className={labelCls}>Announcement</label>
          <textarea className={textareaCls} rows={8} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} />
        </div>
        <div>
          <label className={labelCls}>Send to</label>
          <EmployeeChecklist users={users} selected={selected} onChange={setSelected} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className={buttonSecondaryCls} onClick={onClose}>Cancel</button>
          <button type="submit" className={buttonPrimaryCls} disabled={saving}>{saving ? "Sending…" : `Send to ${selected.size}`}</button>
        </div>
      </form>
    </Modal>
  );
}

function AnnouncementView({ announcement, onOk, busy }) {
  return (
    <Modal onClose={onOk} showClose={false} closeOnEscape={false} closeOnBackdrop={false}
      layer="dialog-top" size="md" align="center" title={null}>
      <div className="flex items-center gap-2 text-brand-700 mb-3">
        <Megaphone className="w-5 h-5" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wider">Announcement</span>
      </div>
      <h2 className="text-lg font-semibold text-ink">{announcement.title}</h2>
      <p className="text-meta text-ink-muted mt-1">
        {announcement.created_by_name ? `${announcement.created_by_name} · ` : ""}{fmt(announcement.created_at)}
      </p>
      <p className="text-sm text-ink whitespace-pre-wrap mt-4 max-w-prose">{announcement.body}</p>
      <div className="flex justify-end mt-6">
        <button type="button" className={buttonPrimaryCls} onClick={onOk} disabled={busy} autoFocus>OK</button>
      </div>
    </Modal>
  );
}

export function AnnouncementSplash() {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [reopened, setReopened] = useState(null);
  const [busy, setBusy] = useState(false);
  // View As is read-only (OK is a POST the server would refuse), so no splash.
  const enabled = Boolean(me && !me.view_as);
  const { data: pending = [] } = useQuery({
    queryKey: ["announcements", "mine", "pending"],
    queryFn: fetchMyPendingAnnouncements,
    enabled,
  });

  useEffect(() => {
    const open = async (e) => {
      try {
        setReopened(await fetchMyAnnouncement(e.detail));
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not open the announcement"));
      }
    };
    window.addEventListener(OPEN_ANNOUNCEMENT_EVENT, open);
    return () => window.removeEventListener(OPEN_ANNOUNCEMENT_EVENT, open);
  }, []);

  if (reopened) return <AnnouncementView announcement={reopened} onOk={() => setReopened(null)} />;
  const current = enabled ? pending[0] : null;
  if (!current) return null;

  async function ok() {
    setBusy(true);
    try {
      await acknowledgeAnnouncement(current.id);
      await queryClient.invalidateQueries({ queryKey: ["announcements", "mine", "pending"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not save — try again"));
    } finally {
      setBusy(false);
    }
  }
  return <AnnouncementView announcement={current} onOk={ok} busy={busy} />;
}

export function AnnouncementsTab({ users }) {
  const { data = [], isLoading } = useQuery({ queryKey: ["announcements", "hr"], queryFn: fetchAnnouncements, staleTime: 0 });
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const columns = [
    { key: "title", label: "Announcement", render: (a) => a.title },
    { key: "by", label: "Sent by", render: (a) => a.created_by_name || "—" },
    { key: "sent", label: "Sent", render: (a) => fmt(a.created_at, "MMM d, yyyy h:mm a") },
    {
      key: "seen", label: "Seen",
      render: (a) => <span className={a.acknowledged_count === a.recipient_count ? "text-success-700" : "text-ink"}>{a.acknowledged_count} / {a.recipient_count}</span>,
    },
  ];

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button type="button" className={buttonPrimarySmCls} onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4" aria-hidden="true" /> New announcement
        </button>
      </div>
      <div className={cardCls}>
        <DataTable columns={columns} rows={data} isLoading={isLoading} rowKey={(a) => a.id}
          emptyText="No announcements yet." onRowClick={setViewing} />
      </div>
      {creating && <NewAnnouncementModal users={users} onClose={() => setCreating(false)} />}
      {viewing && <AnnouncementView announcement={viewing} onOk={() => setViewing(null)} />}
    </div>
  );
}
